import React, { useEffect, useMemo, useState } from 'react';

import ModuloItemForm from './ModuloItemForm';
import BoxConteudo from '../../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../../components/CustomButton';
import CustomCol from '../../../../../../../components/CustomCol';
import CustomTable, {
  type CustomTableHeader,
} from '../../../../../../../components/CustomTable';
import {
  IModuloItemForm,
  IModuloItemRegistro,
  IModuloSubmoduloRegistro,
} from '../../../../../../../modules/types/ModuloSisaf';
import { getModuloItensBySubmoduloId } from '../../../../../../../services/modulosSisaf/moduloItemService';
import theme from '../../../../../../../theme';
import { useExcelExport } from '../../../../../../../utils/excel-export';
import { formatDateToFile } from '../../../../../../../utils/functions';
import { handleDelete } from '../../../../../../../utils/handleDelete';
import { optionStatusBin } from '../../../../Dominios/utils/optionStatusBin';

type ItemTableRow = {
  id: number;
  status: React.ReactNode;
  item: string;
  descricao: string;
};

const headersTable: CustomTableHeader<ItemTableRow>[] = [
  { text: 'ID', key: 'id', width: 10 },
  { text: 'Status', key: 'status', width: 16 },
  { text: 'Item', key: 'item', width: 32 },
  { text: 'Descrição', key: 'descricao', width: 42 },
];

const statusBadge = (status: number) => (
  <span
    style={{
      backgroundColor:
        status === 1 ? theme.colors.verdegov : theme.colors.vermelho,
      borderRadius: '10px',
      padding: '8px 10px',
      display: 'inline-block',
      minWidth: '70px',
      textAlign: 'center',
      color: 'white',
      fontWeight: 600,
    }}
  >
    {optionStatusBin.find((opt) => opt.value === status)?.label || 'N/I'}
  </span>
);

interface ModuloItemSectionProps {
  submodulo: IModuloSubmoduloRegistro;
  onUpdated: () => void;
}

const ModuloItemSection: React.FC<ModuloItemSectionProps> = ({
  submodulo,
  onUpdated,
}) => {
  const [itens, setItens] = useState<IModuloItemRegistro[]>([]);
  const [selectedOffcanvasId, setSelectedOffcanvasId] = useState<number | null>(
    null,
  );
  const [selectedItem, setSelectedItem] = useState<IModuloItemRegistro | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const { exportData, isExporting } = useExcelExport('listagemmoduloitens');

  const normalizeText = (value?: string | null) =>
    (value ?? '').trim().toLowerCase();

  const findCreatedItem = (
    registros: IModuloItemRegistro[],
    preferred?: IModuloItemForm | null,
  ) => {
    if (!preferred) return null;

    if (preferred.id) {
      return registros.find((item) => item.id === preferred.id) || null;
    }

    const itemNome = normalizeText(preferred.item);
    const descricao = normalizeText(preferred.descricao);
    const status = Number(preferred.status);

    const matches = registros.filter(
      (item) =>
        normalizeText(item.item) === itemNome &&
        normalizeText(item.descricao) === descricao &&
        Number(item.status) === status,
    );

    if (matches.length === 0) return null;

    return matches.sort((a, b) => b.id - a.id)[0];
  };

  const fetchItens = async (preferred?: IModuloItemForm | null) => {
    try {
      const registros = await getModuloItensBySubmoduloId(submodulo.id);
      setItens(registros);

      /**
       * Tenta selecionar o item preferido, se fornecido.
       * Rotina utilizada quando cadastrado novo item para selecionar o recém-criado.
       */
      const justCreated = findCreatedItem(registros, preferred);
      if (justCreated) {
        setSelectedItem(justCreated);
        return;
      }

      /**
       * Caso contrário, tenta manter o item atualmente selecionado (se houver).
       */
      if (selectedItem) {
        const atualizado =
          registros.find((item) => item.id === selectedItem.id) || null;
        setSelectedItem(atualizado);
      }
    } catch (error) {
      console.error('Erro ao buscar itens:', error);
      setItens([]);
    }
  };

  useEffect(() => {
    setSelectedOffcanvasId(null);
    setSelectedItem(null);
    setShowForm(false);
    fetchItens();
  }, [submodulo.id]);

  const tableData: ItemTableRow[] = useMemo(
    () =>
      itens.map((item) => ({
        id: item.id,
        status: statusBadge(item.status),
        item: item.item,
        descricao: item.descricao || '—',
      })),
    [itens],
  );

  const handleSelectItem = (row: ItemTableRow) => {
    const registro = itens.find((it) => it.id === row.id) || null;
    setSelectedItem(registro);
    setShowForm(true);
  };

  const handleNew = () => {
    setSelectedItem(null);
    setShowForm(true);
  };

  const handleDeleteWrapper = async (id: number) => {
    const deleted = await handleDelete(id, 'moduloitens');
    if (!deleted) return;

    fetchItens();
    onUpdated();
  };

  const handleExport = async () => {
    try {
      await exportData(
        itens,
        `sisaf_modulos_itens_${formatDateToFile(new Date().toISOString())}.xlsx`,
        undefined,
        'Listagem de Itens',
        'DAF',
      );
    } catch (error) {
      console.error('Erro na exportação de itens:', error);
    }
  };

  return (
    <>
      <BoxConteudo
        boxTitulo={`Itens do Submódulo: ${submodulo.submodulo || ''}`}
        style={{ marginTop: '16px' }}
      >
        <CustomTable
          headers={headersTable}
          data={tableData}
          onRowClick={handleSelectItem}
          showPagination={false}
          filterable={false}
          minHeight="400px"
          data-testid="modulos-submodulos-itens-table"
        />

        <CustomCol className="d-flex justify-content-end gap-3 mt-3">
          <CustomButton
            label="Exportar Dados"
            bgColor={theme.colors.botaoAcao.amarelo}
            textColor={theme.colors.black}
            onClick={handleExport}
            deactivate={isExporting}
          />
          <CustomButton
            label="Novo Item"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={handleNew}
          />
        </CustomCol>
      </BoxConteudo>

      {showForm && (
        <ModuloItemForm
          submoduloId={submodulo.id}
          registro={selectedItem}
          onClose={() => setShowForm(false)}
          asModal
          onSaved={(savedForm) => {
            fetchItens(savedForm);
            onUpdated();
          }}
          onDeleted={(id) => {
            setShowForm(false);
            handleDeleteWrapper(id);
          }}
        />
      )}
    </>
  );
};

export default ModuloItemSection;
